import { useState, useEffect, useContext } from 'react';
import axios from '../../../Axios';
import { Urls } from '../../../Urls';
import { UserApiContext } from '../../UserDetailsContext/UserDetailsContext';

export const SIM_CONSENT_STATUS = {
  PENDING: 'PENDING',
  ALLOWED: 'ALLOWED',
  REJECTED: 'REJECTED',
};

/**
 * Custom hook for managing SIM-based tracking consent
 * @param {string} indentId - The indent ID for which to manage consent
 * @returns {object} Consent state and actions
 */
export const useSimConsent = (indentId) => {
  const [isSendingRequest, setIsSendingRequest] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [vehicleDetails, setVehicleDetails] = useState({});
  const { userDetails } = useContext(UserApiContext);


  // TODO: Check if user has access to SIM tracking feature - Mock for now
  const hasAccess = userDetails?.policy?.indents?.allowed_sim_tracking === "yes" || false;

  // Default: Fetch current consent status on hook initialization
  useEffect(() => {
    if (indentId) {
      fetchSimTrackingStatus(true); // Show loader by default
    }
  }, [indentId]);

  /**
   * Fetch SIM tracking status (with an option to control loader)
   * @param {boolean} showLoader - Whether to show the loader (default: true)
   */
  const fetchSimTrackingStatus = async (showLoader = true) => {
    if (!hasAccess) {
      setError('Access denied: User does not have permission for SIM tracking');
      return;
    }

    if (!indentId) {
      setError('Indent ID is required');
      return;
    }

    if (showLoader) {
      setLoading(true);
    } else {
      setIsCheckingStatus(true);
    }
    setError(null);

    try {
      const { data } = await axios.get(
        `${Urls.indentSimBased}?field=check_consent&indent_id=${indentId}`
      );
      setVehicleDetails(data);
    } catch (err) {
      const errorMessage = err?.response?.data?.message || 'Error fetching SIM tracking status';
      setError(errorMessage);
      console.error('Error fetching SIM tracking status:', err);
    } finally {
      if (showLoader) {
        setLoading(false);
      } else {
        setIsCheckingStatus(false);
      }
    }
  };

  /**
   * Send consent request to driver
   */
  const sendConsentRequest = async () => {
    if (!hasAccess) {
      setError('Access denied: User does not have permission for SIM tracking');
      return { success: false, error: 'Access denied' };
    }

    if (!indentId) {
      setError('Indent ID is required');
      return { success: false, error: 'Indent ID is required' };
    }

    setIsSendingRequest(true);
    setError(null);

    try {
      const url = `${Urls.indentSimBased}?field=send_consent_sms&indent_id=${indentId}`;
      const { data } = await axios.post(url);
      fetchSimTrackingStatus(false);

      return { success: true, data };
    } catch (err) {
      const errorMessage = err?.response?.data?.message || 'Error sending consent request';
      setError(errorMessage);
      console.error('Error sending consent request:', err);
      return { success: false, error: errorMessage };
    } finally {
      setIsSendingRequest(false);
    }
  };

  /**
   * Parse consent status from consent details
   * @param {object} consentDetails - The consent details object
   * @returns {string|null} The consent status or null
   */
  const parseConsentStatus = (consentDetails) => {
    if (!consentDetails || consentDetails?.status !== true) {
      return null;
    }
    return consentDetails?.sim_consent_status || null;
  };

  return {
    // Access Control
    hasAccess,

    // State
    loading,
    isSendingRequest,
    isCheckingStatus,
    error,
    vehicleDetails,

    // Actions
    sendConsentRequest,
    checkConsentStatus: () => fetchSimTrackingStatus(false),
    refetchSimTrackingStatus: () => fetchSimTrackingStatus(false),
    parseConsentStatus,

    // Constants
    SIM_CONSENT_STATUS,
  };
};
